import { useContext, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { 
  Box, 
  Button, 
  ButtonBase,
  Stack, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Tooltip,
  Paper,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  useMediaQuery,
} from '@mui/material';
import { ReportContext } from '../Context';
import { UserContext } from '../Context';
import { SocketContext } from '../Context';
import { ModeContext } from '../Context';
import { ThemeContext } from '../Context';
import { EditingContext } from '../Context';
import { DocumentToolsContext } from '../Context';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import ArticleIcon from '@mui/icons-material/Article';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LoupeIcon from '@mui/icons-material/Loupe';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import axios from 'axios';
// Import de la fonction de téléchargement DOCX
import { downloadReportAsDocx } from './Download';
import History from './History';
import AccessRightsDialog from './AccessRightsDialog';
import HomeQuickAccess from './HomeQuickAccess';
import BlueNotePocketDialog from './BlueNotePocketDialog';
import RepriseDialog from './RepriseDialog';
import ImportISLP from './ImportISLP';
import { useConfirmCountdown } from './useConfirmCountdown';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';

const showFrom = (breakpoint) => ({ display: { xs: 'none', [breakpoint]: 'inline' } });

// Sous `breakpoint`, le libellé est masqué : on retire les marges MUI de l'icône de début
// (marge gauche par défaut : -2px en size="small", -4px en size="medium").
const startIconOnlyBelow = (breakpoint, defaultMarginLeft) => ({
  '& .MuiButton-startIcon': {
    mr: { xs: 0, [breakpoint]: '8px' },
    ml: { xs: 0, [breakpoint]: defaultMarginLeft },
  },
});

const compactActionButtonSx = {
  minWidth: { xs: '36px', xl: '64px' },
  px: { xs: '7px', xl: '9px' },
  ...startIconOnlyBelow('xl', '-2px'),
};

function Up({ connectedUsers = [], totalConnectedUsers = 0 }) {
  const { report, pickReport } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const { socket } = useContext(SocketContext);
  const { mode, handleModeChange } = useContext(ModeContext);
  const { theme } = useContext(ThemeContext);
  const { checkHasUnsavedChanges, resetEditingState } = useContext(EditingContext);
  const { openFindReplace, requestDeleteAllRubrics } = useContext(DocumentToolsContext);
  const muiTheme = useTheme();
  const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const updateHeight = () => setHeaderHeight(el.offsetHeight);
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, reportData, isEditorFullscreen]);

  // Observer pour détecter quand TinyMCE entre en mode fullscreen
  useEffect(() => {
    const checkFullscreen = () => {
      setIsEditorFullscreen(document.body.classList.contains('tox-fullscreen'));
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkFullscreen();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    checkFullscreen();

    return () => observer.disconnect();
  }, []);
  
  // États pour les statistiques des rubriques
  const [rubricCount, setRubricCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [connectedUsersDetails, setConnectedUsersDetails] = useState([]);

  const [downloadNotification, setDownloadNotification] = useState({
    open: false,
    severity: 'success',
    message: ''
  });

  // État pour le dialogue de confirmation de suppression
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    loading: false
  });
  const deleteCountdown = useConfirmCountdown(deleteDialog.open);

  // État pour le dialogue de confirmation de sortie
  const [exitDialog, setExitDialog] = useState({
    open: false
  });

  // Action différée en attente de confirmation (sortie, ouverture d'un autre document, etc.)
  const [pendingAction, setPendingAction] = useState(null);

  // État pour le drawer d'historique
  const [historyOpen, setHistoryOpen] = useState(false);

  // Fenêtre « Droits d'accès »
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);

  // Fenêtre « Importer depuis BlueNote Pocket »
  const [pocketDialogOpen, setPocketDialogOpen] = useState(false);

  // Fenêtres « Reprendre d'un autre document » et « Importer des entités depuis l'ISLP »
  const [repriseDialogOpen, setRepriseDialogOpen] = useState(false);
  const [importISLPOpen, setImportISLPOpen] = useState(false);

  // Menu « Options » (rechercher, remplacer, historique, suppression des rubriques)
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const optionsOpen = Boolean(optionsAnchorEl);

  // Raccourcis de Word : Ctrl+F (rechercher dans la vue affichée) et Ctrl+H (remplacer, vue Édition)
  useEffect(() => {
    const handleShortcut = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      const key = (event.key || '').toLowerCase();
      if (key !== 'f' && key !== 'h') return;
      if ((mode !== 'edit' && mode !== 'read') || rubricCount === 0) return;
      if (document.body.classList.contains('tox-fullscreen')) return;
      // Laisser le navigateur gérer le raccourci dans les fenêtres modales
      const target = event.target;
      if (target instanceof Element && target.closest('.MuiModal-root, .tox-dialog-wrap')) return;
      event.preventDefault();
      openFindReplace(key === 'h' ? 'replace' : 'find');
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [mode, rubricCount, openFindReplace]);

  // Fonction sécurisée pour changer de mode avec vérification
  const safeHandleModeChange = (newMode) => {
    if (handleModeChange && typeof handleModeChange === 'function') {
      const oldMode = mode;
      handleModeChange(newMode);
      
      // Log du changement de mode
      if (oldMode !== newMode) {
        logAction(LOG_ACTIONS.REPORT_MODE_CHANGE, formatReportNumber(report.number, report.year), {
          ancienMode: oldMode,
          nouveauMode: newMode
        });
      }
    } else {
      console.warn('handleModeChange n\'est pas disponible dans le contexte');
    }
  };

  // Charger les données du rapport
  useEffect(() => {
    if (!report) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        setReportData(response.data);
        
        // Calculer les statistiques des rubriques
        if (response.data.rubrics && response.data.rubrics.length > 0) {
          setRubricCount(response.data.rubrics.length);
          
          // Calculer le nombre de mots dans toutes les rubriques
          let totalWords = 0;
          response.data.rubrics.forEach(rubric => {
            // Compter les mots dans le titre
            if (rubric.title) {
              totalWords += rubric.title.trim().split(/\s+/).length;
            }
            
            // Compter les mots dans le contenu
            if (rubric.content) {
              totalWords += rubric.content.trim().split(/\s+/).length;
            }
          });
          
          setWordCount(totalWords);
        } else {
          setRubricCount(0);
          setWordCount(0);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données du document:', error);
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Après une reprise ou un import : ne pas repasser par `loading`, qui démonterait les fenêtres ouvertes
  const reloadReportData = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: report.number, year: report.year },
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Erreur lors du rechargement des données du document:', error);
    }
  };

  // Écouter les mises à jour via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdated = (updatedReport) => {
      if (updatedReport.number === report.number && 
          updatedReport.year === report.year) {
        setReportData(prevData => ({
          ...prevData,
          ...updatedReport,
        }));
        
        // Mettre à jour les statistiques des rubriques si nécessaire
        if (updatedReport.rubrics) {
          setRubricCount(updatedReport.rubrics.length);
          
          // Calculer le nombre de mots dans toutes les rubriques
          let totalWords = 0;
          updatedReport.rubrics.forEach(rubric => {
            // Compter les mots dans le titre
            if (rubric.title) {
              totalWords += rubric.title.trim().split(/\s+/).length;
            }
            
            // Compter les mots dans le contenu
            if (rubric.content) {
              totalWords += rubric.content.trim().split(/\s+/).length;
            }
          });
          
          setWordCount(totalWords);
        }
      }
    };

    socket.on('reportUpdated', handleReportUpdated);

    return () => {
      socket.off('reportUpdated', handleReportUpdated);
    };
  }, [socket, report]);

  // Récupère les infos (couleur, grade, nom) des utilisateurs connectés
  useEffect(() => {
    let isMounted = true;

    const fetchConnectedUsers = async () => {
      if (!connectedUsers || connectedUsers.length === 0) {
        if (isMounted) setConnectedUsersDetails([]);
        return;
      }

      try {
        const token = sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const results = await Promise.all(
          connectedUsers.map(async (matricule) => {
            try {
              const { data } = await axios.get(`/api/user/avatar/${matricule}`, { headers });
              return {
                matricule,
                color: data.nicknameColor || '#9e9e9e',
                rank: (data.rank || '').toUpperCase(),
                firstname: data.firstname || '',
                lastname: (data.lastname || '').toUpperCase(),
              };
            } catch (err) {
              console.error('Erreur lors de la récupération des infos utilisateur:', err);
              return {
                matricule,
                color: '#9e9e9e',
                rank: '...',
                firstname: '...',
                lastname: '',
              };
            }
          })
        );

        if (isMounted) {
          const uniqueByMatricule = results.filter(
            (user, index, self) => self.findIndex(u => u.matricule === user.matricule) === index
          );
          setConnectedUsersDetails(uniqueByMatricule);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs connectés:', error);
        if (isMounted) setConnectedUsersDetails([]);
      }
    };

    fetchConnectedUsers();

    return () => {
      isMounted = false;
    };
  }, [connectedUsers]);

  // Exécute une action de navigation en vérifiant d'éventuelles modifications non sauvegardées
  const handleNavigateWithGuard = (actionFn) => {
    if (typeof actionFn !== 'function') return;
    if (checkHasUnsavedChanges && checkHasUnsavedChanges()) {
      setPendingAction(() => actionFn);
      setExitDialog({ open: true });
    } else {
      resetEditingState();
      actionFn();
    }
  };

  const handleBack = () => {
    handleNavigateWithGuard(() => pickReport(null));
  };

  // Confirmer la sortie malgré les modifications en cours
  const handleConfirmExit = () => {
    resetEditingState();
    setExitDialog({ open: false });
    if (pendingAction) {
      const fn = pendingAction;
      setPendingAction(null);
      fn();
    } else {
      pickReport(null); // Retourne au Dashboard par défaut
    }
  };

  // Annuler la sortie
  const handleCancelExit = () => {
    setExitDialog({ open: false });
    setPendingAction(null);
  };

  // Fonction pour télécharger le rapport au format .docx
  const handleDownloadReport = async () => {
    if (!reportData) return;
    
    const result = await downloadReportAsDocx(reportData);
    
    setDownloadNotification({
      open: true,
      severity: result.success ? 'success' : 'error',
      message: result.message,
    });

    // Log du téléchargement
    if (result.success) {
      logAction(LOG_ACTIONS.REPORT_DOWNLOAD, formatReportNumber(report.number, report.year), {
        format: 'docx',
        rubricCount: rubricCount,
        wordCount: wordCount
      });
    }
  };

  // Fermer la notification de téléchargement
  const handleCloseDownloadNotification = () => {
    setDownloadNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Ouvrir l'historique des modifications
  const handleOpenHistory = () => {
    setHistoryOpen(true);
    // Log de l'ouverture de l'historique
    logAction(LOG_ACTIONS.HISTORY_VIEW, formatReportNumber(report.number, report.year), {
      indice: reportData?.indice || null
    });
  };

  const handleOpenOptions = (event) => {
    setOptionsAnchorEl(event.currentTarget);
  };

  const handleCloseOptions = () => {
    setOptionsAnchorEl(null);
  };

  // openFindReplace bascule en Édition pour Remplacer ; Rechercher reste dans la vue affichée
  const handleOptionFindReplace = (findMode) => {
    setOptionsAnchorEl(null);
    openFindReplace(findMode);
  };

  const handleOptionReprise = () => {
    setOptionsAnchorEl(null);
    setRepriseDialogOpen(true);
  };

  const handleOptionImportISLP = () => {
    setOptionsAnchorEl(null);
    setImportISLPOpen(true);
  };

  const handleOptionPocket = () => {
    setOptionsAnchorEl(null);
    setPocketDialogOpen(true);
  };

  const handleOptionHistory = () => {
    setOptionsAnchorEl(null);
    handleOpenHistory();
  };

  const handleOptionAccessRights = () => {
    setOptionsAnchorEl(null);
    setAccessDialogOpen(true);
  };

  const handleAccessSaved = (access) => {
    if (access) {
      setReportData(prevData => ({ ...prevData, access }));
    }
    setDownloadNotification({
      open: true,
      severity: 'success',
      message: 'Droits d\'accès mis à jour'
    });
  };

  const handleOptionDeleteAllRubrics = () => {
    setOptionsAnchorEl(null);
    if (mode !== 'edit') safeHandleModeChange('edit');
    requestDeleteAllRubrics();
  };

  // Sur petit écran, « Télécharger » et « Supprimer » passent dans le menu Options
  const handleOptionDownload = () => {
    setOptionsAnchorEl(null);
    handleDownloadReport();
  };

  const handleOptionDeleteReport = () => {
    setOptionsAnchorEl(null);
    handleDeleteClick();
  };

  // Ouvrir le dialogue de confirmation de suppression
  const handleDeleteClick = () => {
    setDeleteDialog({
      open: true,
      loading: false
    });
  };

  // Fermer le dialogue de suppression
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      loading: false
    });
  };

  // Confirmer et exécuter la suppression
  const handleConfirmDelete = async () => {
    if (!report) return;
    
    try {
      setDeleteDialog(prev => ({
        ...prev,
        loading: true
      }));

      const token = sessionStorage.getItem('authToken');
      await axios.delete(`/api/report/${report.number}/${report.year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Fermer le dialogue
      setDeleteDialog({
        open: false,
        loading: false
      });

      // Log de la suppression
      logAction(LOG_ACTIONS.REPORT_DELETE, formatReportNumber(report.number, report.year), {
        indice: reportData?.indice || null,
        unit: reportData?.unit || null
      });

      // Afficher une notification de succès
      setDownloadNotification({
        open: true,
        severity: 'success',
        message: 'Document supprimé avec succès'
      });

      // Retourner au dashboard après un délai
      setTimeout(() => {
        pickReport(null);
      }, 1500);

    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      
      setDeleteDialog({
        open: false,
        loading: false
      });

      // Afficher une notification d'erreur
      setDownloadNotification({
        open: true,
        severity: 'error',
        message: error.response?.data?.message || 'Erreur lors de la suppression du document'
      });
    }
  };

  if (loading || !reportData) {
    return <Box sx={{ p: 2 }}>Chargement...</Box>;
  }

  // Ne pas afficher quand TinyMCE est en mode fullscreen
  if (isEditorFullscreen) {
    return null;
  }

  const isRestricted = reportData.access?.type === 'restricted';
  const isDark = theme === 'dark';
  const showEmargementButton = false;
  const availableModes = showEmargementButton 
    ? ['emargement', 'edit', 'read'] 
    : ['edit', 'read'];
  const activeIndex = availableModes.includes(mode) 
    ? availableModes.indexOf(mode) 
    : 0;
  const contrastOnPrimary = muiTheme.palette.getContrastText(muiTheme.palette.primary.main);

  // ring : couleur opaque proche du fond de la capsule, pour détacher les pastilles qui se chevauchent
  const infoBar = {
    text: isDark ? '#e3f2fd' : '#1a253c',
    background: isDark ? 'rgba(100, 181, 246, 0.07)' : 'rgba(255, 255, 255, 0.75)',
    border: isDark ? 'rgba(100, 181, 246, 0.22)' : 'rgba(25, 118, 210, 0.2)',
    divider: isDark ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.16)',
    ring: isDark ? 'rgb(27, 44, 60)' : 'rgb(239, 245, 249)',
  };
  // Teintes de la page : bleus des actions et du mode actif, rouge et vert de Supprimer / Télécharger
  const infoTones = {
    restricted: isDark
      ? { main: '#ff6b6b', soft: 'rgba(244, 67, 54, 0.2)' }
      : { main: muiTheme.palette.error.main, soft: 'rgba(211, 47, 47, 0.12)' },
    open: isDark
      ? { main: '#4ecdc4', soft: 'rgba(78, 205, 196, 0.18)' }
      : { main: muiTheme.palette.success.main, soft: 'rgba(46, 125, 50, 0.12)' },
    rubrics: isDark
      ? { main: '#64b5f6', soft: 'rgba(100, 181, 246, 0.18)' }
      : { main: '#1976d2', soft: 'rgba(25, 118, 210, 0.12)' },
    words: isDark
      ? { main: '#9fa8da', soft: 'rgba(159, 168, 218, 0.18)' }
      : { main: '#425f9e', soft: 'rgba(66, 95, 158, 0.14)' },
    users: isDark
      ? { main: '#4fc3f7', soft: 'rgba(79, 195, 247, 0.18)' }
      : { main: '#0288d1', soft: 'rgba(2, 136, 209, 0.12)' },
  };
  const accessTone = isRestricted ? infoTones.restricted : infoTones.open;
  const infoSegmentSx = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '100%',
    pl: '5px',
    pr: '9px',
    color: infoBar.text,
    fontSize: '0.8rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  };
  const renderInfoIcon = (Icon, tone) => (
    <Box
      component="span"
      sx={{
        width: 20,
        height: 20,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: isDark ? 'rgb(21, 34, 46)' : '#fff',
        backgroundColor: tone.main,
        boxShadow: isDark ? 'none' : '0 1px 3px rgba(26, 37, 60, 0.25)',
      }}
    >
      <Icon sx={{ fontSize: '13px' }} />
    </Box>
  );

  return (
    <Box
      ref={headerRef}
      sx={{
        display: 'grid',
        // Le sélecteur de mode reste centré sur la page tant que la place le permet, sinon il se
        // décale : les colonnes latérales ne descendent jamais sous leur contenu. Sur très petit
        // écran, il se place entre le titre et les actions, et le titre cède en dernier (ellipse).
        // La colonne de gauche inclut la place du cube maison (positionné en absolu).
        gridTemplateColumns: {
          xs: `minmax(${headerHeight}px, max-content) minmax(max-content, 1fr) max-content`,
          sm: 'minmax(max-content, 1fr) auto minmax(max-content, 1fr)',
        },
        alignItems: 'center',
        columnGap: { xs: 1, md: 2 },
        // Hauteur totale de 54px (imposée par Core) : 6 + 42 (sélecteur de mode) + 6
        paddingX: '11px',
        paddingY: '6px',
        background: isDark 
          ? 'rgb(21, 34, 46)'
          : 'rgb(190, 214, 231)',
        position: 'relative',
      }}
    >
      {/* Bouton Maison - Cube pleine hauteur avec panneau d'accès rapide */}
      <HomeQuickAccess
        headerHeight={headerHeight}
        isDark={isDark}
        onHomeClick={handleBack}
        onNavigate={handleNavigateWithGuard}
      />

      {/* Partie gauche */}
      <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ minWidth: 0, pl: `${headerHeight}px` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.1rem', lg: '1.25rem' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              color: isDark ? '#ecf0f1' : 'inherit',
            }}
          >
            <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
            {reportData?.indice || '__'}.
            <SettingsFieldDisplay fieldName="judicialDivision" />.
            {report?.number}/{report?.year}
          </Typography>
        </Box>

        {/* Capsule d'informations : accès, statistiques et utilisateurs connectés */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            height: '30px',
            borderRadius: '999px',
            overflow: 'hidden',
            backgroundColor: infoBar.background,
            border: `1px solid ${infoBar.border}`,
            backdropFilter: 'saturate(160%) blur(8px)',
            boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.25)' : '0 2px 8px rgba(25, 118, 210, 0.12)',
            '& > * + *::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '28%',
              bottom: '28%',
              width: '1px',
              backgroundColor: infoBar.divider,
            },
          }}
        >
          <Tooltip title={`${isRestricted ? 'Accès restreint' : 'Accès non restreint'} - droits d'accès`}>
            <ButtonBase
              onClick={() => setAccessDialogOpen(true)}
              aria-label={`${isRestricted ? 'Accès restreint' : 'Accès non restreint'} - droits d'accès`}
              sx={{
                ...infoSegmentSx,
                pr: '7px',
                transition: 'background-color 0.2s ease',
                '&:hover, &.Mui-focusVisible': { backgroundColor: accessTone.soft },
              }}
            >
              {renderInfoIcon(isRestricted ? LockIcon : LockOpenIcon, accessTone)}
            </ButtonBase>
          </Tooltip>

          {/* Statistiques (sous lg, rubriques et mots passent dans le menu Options) */}
          {isLgUp && (
            <Tooltip title="Nombre de rubriques">
              <Box sx={infoSegmentSx}>
                {renderInfoIcon(ArticleIcon, infoTones.rubrics)}
                {rubricCount}
              </Box>
            </Tooltip>
          )}
          {isLgUp && (
            <Tooltip title="Nombre de mots">
              <Box sx={infoSegmentSx}>
                {renderInfoIcon(TextFieldsIcon, infoTones.words)}
                {wordCount.toLocaleString('fr-BE')}
              </Box>
            </Tooltip>
          )}
          <Tooltip 
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 1 }}>
                  Utilisateurs connectés
                </Typography>
                {connectedUsersDetails.length > 0 ? (
                  <Stack spacing={1}>
                    {connectedUsersDetails.map((user) => {
                      // Calcul des initiales
                      const initials = ((user.lastname?.[0] || '') + (user.firstname?.[0] || '')).toUpperCase() || '?';
                      
                      // Calcul couleur contrastée (simplifié)
                      const getTextColor = (bgColor) => {
                        if (!bgColor || bgColor === '#9e9e9e') return '#fff';
                        const hslMatch = bgColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
                        if (hslMatch) {
                          const h = parseInt(hslMatch[1]);
                          const complementH = (h + 180) % 360;
                          return `hsl(${complementH}, 80%, 20%)`;
                        }
                        return '#fff';
                      };
                      
                      return (
                        <Stack
                          key={user.matricule}
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: user.color,
                              boxShadow: `0 0 3px 1px ${user.color}`,
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {user.rank} {user.lastname} {user.firstname}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic', opacity: 0.7 }}>
                    Aucun utilisateur connecté
                  </Typography>
                )}
              </Box>
            }
            placement="bottom"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(50, 50, 50, 0.95)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  p: 1.5,
                  minWidth: '200px'
                }
              },
              arrow: {
                sx: {
                  color: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(50, 50, 50, 0.95)'
                }
              }
            }}
          >
            <Box sx={{ ...infoSegmentSx, pr: '11px' }}>
              {renderInfoIcon(PeopleIcon, infoTones.users)}
              {totalConnectedUsers}
              {connectedUsersDetails.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: '3px' }}>
                  {connectedUsersDetails.map((user, index) => (
                    <Box
                      key={user.matricule}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: user.color,
                        boxShadow: `0 0 0 2px ${infoBar.ring}`,
                        ml: index === 0 ? 0 : '-3px',
                        zIndex: connectedUsersDetails.length - index,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Tooltip>
        </Box>
      </Stack>

      {/* Partie centrale, centrée tant que les côtés le permettent */}
      <Box sx={{ textAlign: 'center', justifySelf: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: `repeat(${availableModes.length}, 1fr)`,
            gap: '4px',
            width: { xs: '104px', md: '240px', lg: '340px', xl: '440px' },
            borderRadius: '999px',
            px: '4px',
            py: '3px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)'}`,
            backdropFilter: 'saturate(160%) blur(8px)',
            boxShadow: isDark
              ? '0 8px 24px rgba(0,0,0,0.35)'
              : '0 6px 18px rgba(0,0,0,0.12)'
          }}
        >
          {/* Indicateur animé */}
          <Box
            sx={{
              position: 'absolute',
              top: 3,
              bottom: 3,
              left: 4,
              width: `calc((100% - 8px) / ${availableModes.length})`,
              borderRadius: '999px',
              background: `linear-gradient(135deg, ${muiTheme.palette.primary.main}, ${muiTheme.palette.primary.dark})`,
              transform: `translateX(${activeIndex * 100}%)`,
              transition: 'transform 220ms ease',
              boxShadow: isDark
                ? '0 8px 20px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.08)'
                : '0 6px 16px rgba(25,118,210,0.25)'
            }}
          />

          {showEmargementButton && (
            <Button
              onClick={() => safeHandleModeChange('emargement')}
              startIcon={<FilePresentIcon />}
              aria-label="Émargement"
              sx={{
                zIndex: 1,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                height: '34px',
                borderRadius: '999px',
                color: mode === 'emargement' ? contrastOnPrimary : (isDark ? '#c9d1d9' : muiTheme.palette.text.secondary),
                '&:hover': { backgroundColor: 'transparent' },
                minWidth: 0,
                whiteSpace: 'nowrap',
                ...startIconOnlyBelow('md', '-4px')
              }}
            >
              <Box component="span" sx={showFrom('md')}>Émargement</Box>
            </Button>
          )}
          <Button
            onClick={() => safeHandleModeChange('edit')}
            startIcon={<EditIcon />}
            aria-label="Édition"
            sx={{
              zIndex: 1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              height: '34px',
              borderRadius: '999px',
              color: mode === 'edit' ? contrastOnPrimary : (isDark ? '#c9d1d9' : muiTheme.palette.text.secondary),
              '&:hover': { backgroundColor: 'transparent' },
              minWidth: 0,
              whiteSpace: 'nowrap',
              ...startIconOnlyBelow('md', '-4px')
            }}
          >
            <Box component="span" sx={showFrom('md')}>Édition</Box>
          </Button>
          <Button
            onClick={() => safeHandleModeChange('read')}
            startIcon={<MenuBookIcon />}
            aria-label="Lecture"
            sx={{
              zIndex: 1,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              height: '34px',
              borderRadius: '999px',
              color: mode === 'read' ? contrastOnPrimary : (isDark ? '#c9d1d9' : muiTheme.palette.text.secondary),
              '&:hover': { backgroundColor: 'transparent' },
              minWidth: 0,
              whiteSpace: 'nowrap',
              ...startIconOnlyBelow('md', '-4px')
            }}
          >
            <Box component="span" sx={showFrom('md')}>Lecture</Box>
          </Button>
        </Box>
      </Box>

      {/* Section droite - Actions */}
      <Stack direction="row" alignItems="center" sx={{ justifyContent: 'flex-end' }}>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Importer depuis BlueNote Pocket">
            <Button
              startIcon={<SmartphoneIcon />}
              onClick={() => setPocketDialogOpen(true)}
              variant="contained"
              size="small"
              disableElevation
              aria-label="Importer depuis BlueNote Pocket"
              sx={{
                ...compactActionButtonSx,
                px: { xs: '7px', md: '10px' },
                ...startIconOnlyBelow('md', '-2px'),
                height: '32px',
                textTransform: 'none',
                fontWeight: 600,
                color: contrastOnPrimary,
                backgroundColor: muiTheme.palette.primary.main,
                boxShadow: isDark ? 'none' : '0 2px 6px rgba(26, 37, 60, 0.3)',
                '&:hover': {
                  backgroundColor: muiTheme.palette.primary.dark,
                  boxShadow: isDark ? 'none' : '0 3px 10px rgba(26, 37, 60, 0.4)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box component="span" sx={showFrom('md')}>Pocket</Box>
            </Button>
          </Tooltip>
          <Tooltip title="Rechercher, remplacer, historique, droits d'accès...">
            <Button
              id="report-options-button"
              aria-controls={optionsOpen ? 'report-options-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={optionsOpen ? 'true' : undefined}
              startIcon={<TuneIcon />}
              endIcon={
                <ArrowDropDownIcon
                  sx={{
                    transform: optionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                />
              }
              onClick={handleOpenOptions}
              variant="outlined"
              size="small"
              sx={{
                ...compactActionButtonSx,
                textTransform: 'none',
                fontWeight: 500,
                height: '32px',
                borderColor: isDark ? '#4fc3f7' : '#0288d1',
                color: optionsOpen ? 'white' : (isDark ? '#4fc3f7' : '#0288d1'),
                backgroundColor: optionsOpen
                  ? (isDark ? '#4fc3f7' : '#0288d1')
                  : (isDark ? 'rgba(79, 195, 247, 0.05)' : 'transparent'),
                '& .MuiButton-endIcon': { ml: 0.25 },
                '&:hover': {
                  backgroundColor: isDark ? '#4fc3f7' : '#0288d1',
                  color: 'white',
                  borderColor: isDark ? '#4fc3f7' : '#0288d1',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box component="span" sx={showFrom('xl')}>Options</Box>
            </Button>
          </Tooltip>
          <Menu
            id="report-options-menu"
            anchorEl={optionsAnchorEl}
            open={optionsOpen}
            onClose={handleCloseOptions}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            MenuListProps={{ 'aria-labelledby': 'report-options-button', dense: true }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 0.75,
                minWidth: 270,
                borderRadius: '12px',
                backgroundColor: isDark ? '#2d3748' : '#fff',
                backgroundImage: 'none',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                boxShadow: isDark
                  ? '0 10px 30px rgba(0, 0, 0, 0.5)'
                  : '0 10px 30px rgba(0, 0, 0, 0.12)',
                '& .MuiList-root': { py: 0.75 },
                '& .MuiMenuItem-root': {
                  mx: 0.75,
                  px: 1.25,
                  py: 0.875,
                  borderRadius: '8px',
                  color: isDark ? '#e2e8f0' : '#2d3748',
                  transition: 'background-color 0.15s ease',
                  '&:hover, &.Mui-focusVisible': {
                    backgroundColor: isDark ? 'rgba(79, 195, 247, 0.12)' : 'rgba(2, 136, 209, 0.08)',
                  },
                  '&.Mui-disabled': { opacity: 0.45 },
                },
                '& .MuiListItemIcon-root': {
                  minWidth: 34,
                  color: isDark ? '#4fc3f7' : '#0288d1',
                },
                '& .MuiListItemText-primary': { fontSize: '0.875rem', fontWeight: 500 },
                '& .MuiMenuItem-root.options-danger': {
                  color: isDark ? '#ff6b6b' : muiTheme.palette.error.main,
                  '&:hover, &.Mui-focusVisible': {
                    backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(211, 47, 47, 0.08)',
                  },
                },
                '& .options-danger .MuiListItemIcon-root': { color: 'inherit' },
                '& .MuiDivider-root': {
                  my: 0.75,
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                },
              }
            }}
          >
            {!isLgUp && (
              <ListSubheader
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 0.5,
                  lineHeight: 1.5,
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  backgroundColor: 'transparent',
                  color: isDark ? '#a0aec0' : 'text.secondary',
                }}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <ArticleIcon sx={{ fontSize: '1rem', color: isDark ? '#42a5f5' : '#1976d2' }} />
                  {`${rubricCount} rubrique${rubricCount > 1 ? 's' : ''}`}
                </Box>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <TextFieldsIcon sx={{ fontSize: '1rem', color: isDark ? '#66bb6a' : '#4caf50' }} />
                  {`${wordCount} mot${wordCount > 1 ? 's' : ''}`}
                </Box>
              </ListSubheader>
            )}
            {!isLgUp && <Divider />}
            <MenuItem onClick={() => handleOptionFindReplace('find')} disabled={rubricCount === 0}>
              <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Rechercher</ListItemText>
              <Typography variant="caption" sx={{ ml: 3, color: isDark ? '#a0aec0' : 'text.secondary' }}>
                Ctrl+F
              </Typography>
            </MenuItem>
            <MenuItem onClick={() => handleOptionFindReplace('replace')} disabled={rubricCount === 0}>
              <ListItemIcon><FindReplaceIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Remplacer</ListItemText>
              <Typography variant="caption" sx={{ ml: 3, color: isDark ? '#a0aec0' : 'text.secondary' }}>
                Ctrl+H
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleOptionReprise}>
              <ListItemIcon><LoupeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Reprendre d'un autre document</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOptionImportISLP}>
              <ListItemIcon><FileUploadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Importer des entités depuis l'ISLP</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOptionPocket}>
              <ListItemIcon><SmartphoneIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Importer depuis BlueNote Pocket</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleOptionHistory}>
              <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Historique des modifications</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOptionAccessRights}>
              <ListItemIcon><LockPersonIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Droits d'accès</ListItemText>
              <Typography
                variant="caption"
                sx={{
                  ml: 3,
                  fontWeight: 600,
                  color: isRestricted
                    ? (isDark ? '#ff6b6b' : muiTheme.palette.error.main)
                    : (isDark ? '#4ecdc4' : muiTheme.palette.success.main)
                }}
              >
                {isRestricted ? 'Restreint' : 'Non-restreint'}
              </Typography>
            </MenuItem>
            {!isMdUp && (
              <MenuItem onClick={handleOptionDownload}>
                <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Télécharger en format Word</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem
              className="options-danger"
              onClick={handleOptionDeleteAllRubrics}
              disabled={rubricCount === 0}
            >
              <ListItemIcon><DeleteSweepIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Supprimer toutes les rubriques</ListItemText>
            </MenuItem>
            {!isMdUp && (
              <MenuItem className="options-danger" onClick={handleOptionDeleteReport}>
                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Supprimer le document</ListItemText>
              </MenuItem>
            )}
          </Menu>
          {isMdUp && (
            <>
              <Tooltip title="Supprimer le document">
                <Button
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                  variant="outlined"
                  size="small"
                  sx={{
                    ...compactActionButtonSx,
                    textTransform: 'none',
                    fontWeight: 500,
                    height: '32px',
                    borderColor: isDark ? '#f44336' : muiTheme.palette.error.main,
                    color: isDark ? '#f44336' : muiTheme.palette.error.main,
                    backgroundColor: isDark ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
                    '&:hover': {
                      backgroundColor: isDark ? '#f44336' : muiTheme.palette.error.main,
                      color: 'white',
                      borderColor: isDark ? '#f44336' : muiTheme.palette.error.main,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box component="span" sx={showFrom('xl')}>Supprimer</Box>
                </Button>
              </Tooltip>
              <Tooltip title="Télécharger en format Word">
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadReport}
                  variant="outlined"
                  size="small"
                  sx={{
                    ...compactActionButtonSx,
                    textTransform: 'none',
                    fontWeight: 500,
                    height: '32px',
                    borderColor: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                    color: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                    backgroundColor: isDark ? 'rgba(102, 187, 106, 0.05)' : 'transparent',
                    '&:hover': {
                      backgroundColor: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                      color: 'white',
                      borderColor: isDark ? '#66bb6a' : muiTheme.palette.success.main,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box component="span" sx={showFrom('xl')}>Télécharger</Box>
                </Button>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      {/* Notification de téléchargement */}
      <Snackbar 
        open={downloadNotification.open} 
        autoHideDuration={3000} 
        onClose={handleCloseDownloadNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseDownloadNotification} 
          severity={downloadNotification.severity}
          variant="filled"
        >
          {downloadNotification.message}
        </Alert>
      </Snackbar>

      {/* Dialogue de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: isDark ? '#2d3748' : '#fff',
            color: isDark ? '#fff' : 'inherit',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: isDark ? '#ff6b6b' : muiTheme.palette.error.main,
            fontWeight: 'bold'
          }}
        >
          <DeleteIcon />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir supprimer définitivement ce document ?
          </Typography>
          <Typography variant="body2" sx={{ 
            fontWeight: 'bold',
            color: isDark ? '#ecf0f1' : 'inherit'
          }}>
            <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
            {reportData?.indice || '__'}.
            <SettingsFieldDisplay fieldName="judicialDivision" />.
            {report?.number}/{report?.year}
          </Typography>
          <Typography variant="body2" sx={{ 
            mt: 2,
            color: isDark ? '#ff6b6b' : muiTheme.palette.error.main,
            fontStyle: 'italic'
          }}>
            Cette action est irréversible et supprimera toutes les données associées au document.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={deleteDialog.loading}
            sx={{
              borderColor: isDark ? '#4a5568' : muiTheme.palette.grey[400],
              color: isDark ? '#cbd5e0' : muiTheme.palette.text.secondary,
              '&:hover': {
                borderColor: isDark ? '#718096' : muiTheme.palette.grey[600],
              }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteDialog.loading || deleteCountdown > 0}
            sx={{
              backgroundColor: isDark ? '#f44336' : muiTheme.palette.error.main,
              '&:hover': {
                backgroundColor: isDark ? '#d32f2f' : muiTheme.palette.error.dark,
              },
              '&:disabled': {
                backgroundColor: isDark ? '#4a4a4a' : muiTheme.palette.action.disabled,
              }
            }}
          >
            {deleteDialog.loading
              ? 'Suppression...'
              : `Supprimer définitivement${deleteCountdown > 0 ? ` (${deleteCountdown})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation de sortie avec modifications en cours */}
      <Dialog
        open={exitDialog.open}
        onClose={handleCancelExit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: isDark ? '#2d3748' : '#fff',
            color: isDark ? '#fff' : 'inherit',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: isDark ? '#ffb74d' : muiTheme.palette.warning.main,
            fontWeight: 'bold'
          }}
        >
          <ArrowBackIcon />
          Modifications en cours
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Vous avez des modifications ou des générations en cours qui seront perdues si vous quittez maintenant.
          </Typography>
          <Typography variant="body2" sx={{ 
            color: isDark ? '#ffb74d' : muiTheme.palette.warning.main,
            fontStyle: 'italic'
          }}>
            Voulez-vous vraiment quitter et perdre ces modifications ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCancelExit}
            variant="outlined"
            sx={{
              borderColor: isDark ? '#4a5568' : muiTheme.palette.grey[400],
              color: isDark ? '#cbd5e0' : muiTheme.palette.text.secondary,
              '&:hover': {
                borderColor: isDark ? '#718096' : muiTheme.palette.grey[600],
              }
            }}
          >
            Rester et continuer
          </Button>
          <Button
            onClick={handleConfirmExit}
            variant="contained"
            sx={{
              backgroundColor: isDark ? '#ff9800' : muiTheme.palette.warning.main,
              '&:hover': {
                backgroundColor: isDark ? '#f57c00' : muiTheme.palette.warning.dark,
              }
            }}
          >
            Quitter malgré tout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawer Historique */}
      <History 
        open={historyOpen} 
        onClose={() => setHistoryOpen(false)} 
        reportData={reportData} 
      />

      <AccessRightsDialog
        open={accessDialogOpen}
        onClose={() => setAccessDialogOpen(false)}
        reportData={reportData}
        onSaved={handleAccessSaved}
      />

      <BlueNotePocketDialog
        open={pocketDialogOpen}
        onClose={() => setPocketDialogOpen(false)}
        report={reportData}
        reloadReportData={reloadReportData}
      />

      <RepriseDialog
        open={repriseDialogOpen}
        onClose={() => setRepriseDialogOpen(false)}
        report={reportData}
        reloadReportData={reloadReportData}
      />

      <ImportISLP
        open={importISLPOpen}
        onClose={() => setImportISLPOpen(false)}
        report={reportData}
        reloadReportData={reloadReportData}
      />
    </Box>
  );
}

export default Up;
